import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import SetPasswordForm from "./SetPasswordForm";
import { useSetPassword } from "../hooks/PasswordHooks";
import { useCheckPasswordToken } from "../hooks/PasswordHooks";
import { ClipLoader } from "react-spinners";
import ValidationSummary from "../ValidationSummary";
import { toast } from "react-toastify";

/**
 * SetPasswordPage component handles the password reset flow. Validates the token from URL and sets the new password.
 * Shows success message on successful password reset or error message on failure.
 */
const SetPasswordPage = () => {
    const { t } = useTranslation();
    const [searchParams] = useSearchParams();
    const passwordResetToken = searchParams.get('passwordResetToken');
    const [isResetSuccess, setIsResetSuccess] = useState(false);

    if (!passwordResetToken) {
        return (
            <div className="container mt-4">
                <div className="alert alert-danger">
                    {t('password.missingToken')}
                </div>
            </div>
        );
    }

    const { isError } = useCheckPasswordToken(passwordResetToken);
    const setPasswordMutation = useSetPassword(passwordResetToken);

    if (isError) {
        return (
            <div className="container mt-4">
                <div className="alert alert-danger">
                    {t('password.invalidToken')}
                </div>
            </div>
        );
    }

    if (isResetSuccess) {
        return (
            <div className="container mt-4">
                <div className="alert alert-success">
                    <div className="mt-2">
                        {t('password.changedSuccess')}
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="container mt-4">
            {setPasswordMutation.isError && (
                <ValidationSummary error={setPasswordMutation.error} />
            )}
            {setPasswordMutation.isPending ? (
                <div className="spinner-container" style={{ textAlign: 'center', marginTop: '20px' }}>
                    <ClipLoader size={50} color={"#123abc"} loading={true} />
                    <p>{t('password.settingPassword')}</p>
                </div>
            ) : (
                <SetPasswordForm
                    passwordResetToken={passwordResetToken}
                    submitted={(password: string) => {
                        setPasswordMutation.mutate(password, {
                            onError: () => {
                                toast.error(t('password.changedError'));
                            },
                            onSuccess: () => {
                                toast.success(t('password.changedSuccess'));
                                setIsResetSuccess(true);
                            }
                        });
                    }}
                />
            )}
        </div>
    );
};


export default SetPasswordPage;